import { parseApiDate } from '@/lib/dates';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage, usePartnerAuthStore } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusPill } from '@/components/groups/StatusPill';
import { InvoiceActions } from '@/components/invoices/InvoiceActions';
import { formatCurrencyAmount } from '@/lib/formatMoney';
import type { InvoiceListMeta, InvoiceSummary } from '@/types/invoices';

const ALL = 'all';
const SEARCH_DEBOUNCE_MS = 300;

function formatDate(iso: string | null): string {
  return iso ? parseApiDate(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set';
}

export function InvoicesPage() {
  const { partner } = usePartnerAuthStore();
  const isAdmin = partner?.role === 'admin';
  const [searchParams] = useSearchParams();
  // A group uuid arrives from the "View invoices" link on group detail.
  const groupFilter = searchParams.get('group') ?? '';

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [meta, setMeta] = useState<InvoiceListMeta | null>(null);
  const [status, setStatus] = useState(ALL);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  /**
   * Bumped from an event handler to ask for a refetch, after an invoice action
   * has changed a row.
   */
  const [reloadCount, setReloadCount] = useState(0);
  /**
   * The filter combination currently on screen, and the one the last settled
   * response was for. Deriving isLoading from the two means the flag never has
   * to be switched on from inside the effect.
   */
  const requestKey = JSON.stringify([status, debouncedSearch, page, groupFilter, reloadCount]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const isLoading = loadedKey !== requestKey;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const buildParams = useCallback(
    (statusFilter: string, term: string, pageNumber: number) => {
      const params = new URLSearchParams({ page: String(pageNumber) });
      if (statusFilter !== ALL) {
        params.set('status', statusFilter);
      }
      if (term) {
        params.set('search', term);
      }
      if (groupFilter) {
        params.set('group', groupFilter);
      }
      return params;
    },
    [groupFilter],
  );

  useEffect(() => {
    // `active` is what makes a superseded response harmless: the cleanup runs
    // before the next fetch starts and on unmount, so a slow early response can
    // no longer repaint the table with results the user has already moved past.
    let active = true;

    void (async () => {
      try {
        const response = await api.get(`/invoices?${buildParams(status, debouncedSearch, page).toString()}`);
        if (!active) return;
        setInvoices(response.data.data);
        setMeta(response.data.meta);
      } catch (error) {
        if (!active) return;
        // Without this the table just stays empty, which reads identically to a
        // deployment that genuinely has no invoices.
        setInvoices([]);
        setMeta(null);
        toast.error(getApiErrorMessage(error), { id: 'invoices-error' });
      }
      if (active) setLoadedKey(requestKey);
    })();

    return () => {
      active = false;
    };
  }, [buildParams, status, debouncedSearch, page, requestKey]);

  const exportCsv = async () => {
    try {
      const params = buildParams(status, debouncedSearch, 1);
      params.delete('page');
      const response = await api.get(`/invoices/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invoices.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error), { id: 'invoices-export-error' });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Invoices</h1>
        <p className="text-muted-foreground">Every invoice raised on the platform, newest due date first.</p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <Input
                className="w-[220px]"
                placeholder="Search by invoice number"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]" aria-label="Filter by status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All statuses</SelectItem>
                  {(meta?.statuses ?? []).map((option) => (
                    <SelectItem key={option} value={option} className="capitalize">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              {meta && (
                <p className="text-sm text-muted-foreground">
                  {meta.total} {meta.total === 1 ? 'invoice' : 'invoices'}
                </p>
              )}
              <Button variant="outline" size="sm" onClick={exportCsv}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>

          {invoices.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {isLoading ? 'Loading invoices.' : 'No invoices match these filters.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    {/*
                      Shown to both roles: the menu always carries Preview,
                      which is a read. Only the mutating items are admin-gated,
                      inside InvoiceActions.
                    */}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.invoiceNumber}>
                      <TableCell className="whitespace-nowrap font-medium">{invoice.invoiceNumber}</TableCell>
                      <TableCell className="whitespace-nowrap">{invoice.group?.name ?? 'Unknown'}</TableCell>
                      <TableCell className="whitespace-nowrap">{invoice.tierName ?? 'Not set'}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(invoice.periodStart)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(invoice.dueAt)}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatCurrencyAmount(invoice.amount, invoice.currency)}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={invoice.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <InvoiceActions
                          invoice={invoice}
                          canWrite={isAdmin}
                          onDone={() => setReloadCount((count) => count + 1)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {meta && meta.lastPage > 1 && (
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {meta.currentPage} of {meta.lastPage}
              </span>
              <Button variant="outline" size="sm" disabled={page >= meta.lastPage} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
